import {
  Component,
  DoCheck,
  Inject,
  Input,
  OnInit,
  forwardRef
} from '@angular/core';
import { TranslateService }                 from '@ngx-translate/core';
import { UIRouter }                         from '@uirouter/core';

import { ApiService }             from '../api/api.service';
import { CodeTypeModel }          from '../core/models/code-type';
import { ConfirmService }         from '../messages/confirm/confirm.service';
import { JsonService }            from '../core/services/json.service';
import { MessagesService }        from '../messages/messages.service';
import { ModalService }           from '../modals/modal.service';
import { InspectionTemplate }     from './inspection.template.service';
import { InspectionTemplateModel } from './models/inspection.template';

@Component({
  selector: 'wor-inspection-template',
  template: require('./inspection.template.component.html')
})
export class InspectionTemplateComponent implements OnInit, DoCheck {
  @Input() groups: Array<CodeTypeModel>;
  @Input() template: InspectionTemplateModel;
  @Input() types: Array<CodeTypeModel>;

  group: CodeTypeModel;
  inspectionTemplate: InspectionTemplate;
  originalGroups: Array<CodeTypeModel>;

  private lastGroupsLength = -1;

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => ConfirmService)) private confirmService: ConfirmService,
    @Inject(forwardRef(() => JsonService)) private jsonService: JsonService,
    @Inject(forwardRef(() => MessagesService)) private messagesService: MessagesService,
    @Inject(forwardRef(() => ModalService)) private modalService: ModalService,
    private translateService: TranslateService,
    private uiRouter: UIRouter
  ) {}

  ngOnInit(): void {
    this.originalGroups    = this.jsonService.copy(this.groups);
    this.inspectionTemplate = new InspectionTemplate(this.template, this.groups, this.types, this.apiService);
    this.updateGroupOptions();
  }

  ngDoCheck(): void {
    if (this.inspectionTemplate && this.inspectionTemplate.groups.length !== this.lastGroupsLength) {
      this.lastGroupsLength = this.inspectionTemplate.groups.length;
      this.updateGroupOptions();
    }
  }

  addGroup(): void {
    this.inspectionTemplate.addGroup(this.group);
    this.group = undefined;
  }

  copy(): void {
    this.modalService.capture({
      capture: 'Name of Template',
      title  : 'Copy Inspection Template',
      value  : ''
    })
    .then(name => this.inspectionTemplate.copy(name))
    .then(result => {
      this.inspectionTemplate = new InspectionTemplate(result, this.groups, this.types, this.apiService);
      this.messagesService.show('Inspection Template Copied.', 'success');
    })
    .catch(err => this.messagesService.error(err));
  }

  save(): void {
    this.inspectionTemplate.save()
      .then((template: InspectionTemplateModel) => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.TEMPLATE'), 'success');

        if (this.inspectionTemplate.id) {
          this.uiRouter.stateService.reload();
        } else {
          this.uiRouter.stateService.go('app.inspection-template', { id: template.id });
        }
      })
      .catch(err => {
        this.messagesService.error(err);
      })
      .finally(() => {
        this.uiRouter.stateService.reload();
      });
  }

  deleteTemplate(): void {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_TEMPLATE'))
      .then(() => this.apiService.delete('/inspection_template', this.inspectionTemplate.id))
      .then(() => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.TEMPLATE'), 'success');
        this.uiRouter.stateService.go('app.inspection-templates');
      })
      .catch(err => {
        this.messagesService.error(err);
      });
  }

  private updateGroupOptions(): void {
    if (!this.inspectionTemplate) return;

    const originalGroups = this.jsonService.copy(this.originalGroups);
    const groupCodes = this.inspectionTemplate.groups.map((g) => g.code);

    this.inspectionTemplate.groupOptions = originalGroups.filter(
      (group: CodeTypeModel) => groupCodes.indexOf(group.code) < 0
    );
  }
}
